import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import {
  SG_ADD_TASK_TEMPLATE,
  SG_CREATE_TASK_SCHEDULE,
  CLEAR_TASK_TEMPLATE,
  SG_FETCH_TASK_TEMPLATE,
  SG_UPDATE_TASK_TEMPLATE,
  SG_ADD_EMAIL_TEMPLATE,
  SG_ADD_USER_TASK,
  SG_ADD_EMPLOYEE_REPORT_ACCESS,
} from "constants/actions";
import { useNavigate } from "react-router";
import { useToasts } from "react-toast-notifications";
import { v4 as uuidv4 } from "uuid";
import { useUserSearchContext } from "Providers/UserSearchProvider";

// Create the context
const SurveyContext = createContext();

// Custom hook to use the context
export const useSurveyContext = () => useContext(SurveyContext);

// Provider component
export const SurveyProvider = ({
  children,
  initialFormData,
  surveyId,
  mode,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const { userList } = useUserSearchContext();

  // State management
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    feedbackTipsEnabled: false,
    questions:
      mode === "edit"
        ? []
        : [
            {
              id: uuidv4(),
              question: "",
              aiEnhanced: true,
              task_type: "question",
              type: { value: "text", label: "Open Text" },
            },
          ],
    assignedUsersList: [],
    collaborators: [],
    user_tasks: [
      {
        task_type: "survey_is_anonymous",
        value: true,
      },
    ],
    emailSubject: "",
    emailBody: "",
    // Only merge initialFormData when in edit mode to prevent previous template data from persisting in create mode
    ...(mode === "edit" ? initialFormData : {}),
  });

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [error, setError] = useState(null);

  // Track if we're sending a survey (via handleSubmit) vs saving a draft (via saveDraft)
  const isSendingRef = useRef(false);

  // Validation function
  const validateFormData = (ignoreFields = []) => {
    const validationErrors = {};

    if (!ignoreFields.includes("name") && !(formData.name || "").trim()) {
      validationErrors.surveyNameValidation = "Survey name cannot be empty";
    }

    if (!ignoreFields.includes("description")) {
      // Check for description in both direct property and user_tasks
      const directDescription = (formData.description || "").trim();
      const userTasksDescription = formData.user_tasks?.find(
        (task) => task && task.task_type === "description"
      )?.text || "";
      const hasDescription = directDescription || userTasksDescription.trim();
      
      if (!hasDescription) {
        validationErrors.surveyDescriptionValidation =
          "Survey must have a description";
      }
    }

    if (
      !ignoreFields.includes("questions") &&
      formData.questions.some((q) => !(q.question || "").trim())
    ) {
      validationErrors.surveyQuestionValidation = "Question cannot be empty";
    }

    if (
      !ignoreFields.includes("emailSubject") &&
      !(formData.emailSubject || "").trim()
    ) {
      validationErrors.emailSubjectValidation = "Email subject cannot be empty";
    }

    if (!ignoreFields.includes("emailBody") && !(formData.emailBody || "").trim()) {
      validationErrors.emailBodyValidation = "Email body cannot be empty";
    }

    if (Object.keys(validationErrors).length > 0) {
      addToast("Unable to save survey. Please check the form for errors", {
        appearance: "error",
      });
    }

    return validationErrors;
  };

  // Redux selectors
  const {
    get_auth,
    createdTemplate,
    createdScheduledTemplate,
    fetchedTemplate,
    updatedTemplate,
    createdEmailTemplate,
    assignedUserTasks,
    draftSend,
    addedReportAccess,
  } = useSelector(
    (state) => ({
      get_auth: state.auth,
      createdTemplate: state.task_templates?.created,
      createdScheduledTemplate: state.task_templates?.created_scheduled_task,
      fetchedTemplate: state.task_templates?.fetched_template,
      updatedTemplate: state.task_templates?.updated_template,
      createdEmailTemplate: state.email_templates?.created_email_template,
      updatedEmailTemplate: state.email_templates?.updated_email_template,
      assignedUserTasks: state.task_templates?.assigned_user_tasks,
      draftSend: state.task_templates?.draftSend,
      addedReportAccess: state.task_templates?.added_report_access,
    }),
    shallowEqual
  );

  // Fetch survey data when in edit mode, clear when in create mode
  useEffect(() => {
    if (mode === "edit" && surveyId) {
      dispatch({ type: SG_FETCH_TASK_TEMPLATE, payload: surveyId });
    } else if (mode === "create") {
      // Clear any existing template data from Redux when entering create mode
      dispatch({ type: CLEAR_TASK_TEMPLATE });
    }
  }, [mode, surveyId, dispatch]);

  // Update form data when fetchedTemplate changes - but only in edit mode
  useEffect(() => {
    if (fetchedTemplate && fetchedTemplate.id && mode === "edit") {
      const assigneesList =
        fetchedTemplate.user_tasks.find(
          (task) => task && task.task_type === "assignee_draft"
        )?.assignees || [];

      const collaboratorsTask = fetchedTemplate.user_tasks.find(
        (task) => task && task.task_type === "collaborators"
      );

      const collaboratorsList = collaboratorsTask?.collaborators || [];

      // Convert collaborator IDs to user objects
      const collaboratorUsers = collaboratorsList.map(id => {
        const user = userList?.find(u => u.value === id);
        return user ? {
          key: user.value,
          value: user.value,
          text: user.text,
          id: user.value
        } : null;
      }).filter(Boolean);

      // Check if this is a draft - templates should NEVER be treated as drafts
      const isTemplate = fetchedTemplate.is_template === true;
      const isDraftTask = fetchedTemplate.user_tasks.find(
        (task) => task && task.task_type === "is_draft" && task.value === true
      );
      
      // Only consider it a draft if it's NOT a template AND has the draft task
      const isActualDraft = !isTemplate && Boolean(isDraftTask);

      const initialFormData = {
        name: fetchedTemplate.name || "",
        id: fetchedTemplate.id, // Always include the ID
        isExistingDraft: isActualDraft, // Set draft flag only for actual drafts, not templates
        draftData: {
          name: fetchedTemplate.name || "",
          description:
            fetchedTemplate.user_tasks.find(
              (task) => task && task.task_type === "description"
            )?.text || "",
          feedbackTipsEnabled: fetchedTemplate.feedbackTipsEnabled || false,
          emailBody: fetchedTemplate.email_content || "",
          emailSubject: fetchedTemplate.email_subject || "",
          survey_is_anonymous: fetchedTemplate.user_tasks.find(
            (task) => task && task.task_type === "survey_is_anonymous"
          )?.value,
        },
        feedbackTipsEnabled: fetchedTemplate.feedbackTipsEnabled || false,
        questions: fetchedTemplate?.user_tasks
          ?.filter((task) => task && task.task_type === "question")
          ?.map((task) => ({
            ...task,
            id: task.id,
            question: task.question,
            aiEnhanced: task.aiEnhanced,
            task_type: "question",
          })),
        assignedUsersList: [],
        assignedUsersDraft: assigneesList.length > 0 ? [...assigneesList] : [],
        collaborators: collaboratorUsers,
        emailSubject:
          fetchedTemplate.user_tasks.find(
            (task) => task && task.task_type === "email_draft"
          )?.subject || "",
        emailBody:
          fetchedTemplate.user_tasks.find(
            (task) => task && task.task_type === "email_draft"
          )?.body || "",
        user_tasks: fetchedTemplate.user_tasks || [],
      };

      setFormData(initialFormData);
    }
  }, [fetchedTemplate, mode]);

  // Update form data function
  const updateFormData = useCallback((updatedSection) => {
    setFormData((prev) => ({ ...prev, ...updatedSection }));
  }, []);

  // Effect to update formData with draft ID when template is created for draft save
  useEffect(() => {
    if (createdTemplate?.id && !formData.id) {
      // Update formData with the draft ID so it can be used for subsequent operations
      setFormData(prev => ({ ...prev, id: createdTemplate.id, isExistingDraft: true }));
    }
  }, [createdTemplate?.id]);


  const saveDraft = (isTemplate = true) => {
    // Mark that we're saving a draft, not sending
    isSendingRef.current = false;
    setLoading(true);
    setError(null);

    const validationErrors = validateFormData(["emailSubject", "emailBody"]);
    if (Object.keys(validationErrors).length > 0) {
      setError(validationErrors);
      setLoading(false);
      return;
    }

    const { assignedUsersList, assignedUsersDraft, emailBody, emailSubject, collaborators, isExistingDraft, ...restOfFormData } =
      formData;

    // Common objects
    const emailDraft = {
      task_type: "email_draft",
      subject: restOfFormData.emailSubject || emailSubject,
      body: restOfFormData.emailBody || emailBody,
    };

    // Concatenate the assignedUsersList and assignedUsersDraft removing duplicates
    const updateUserList = assignedUsersList;
    const existingUserList = assignedUsersDraft;

    // If the updateUserList is not empty use it, otherwise use the existingUserList
    const assigneesList = updateUserList.length > 0 ? updateUserList : existingUserList;

    const assigneeDraft = {
      task_type: "assignee_draft",
      assignees: assigneesList,
    };

    const collaboratorsDraft = {
      task_type: "collaborators",
      collaborators: collaborators.map(user => user.value),
    };

    const surveyIsAnonymous = restOfFormData.user_tasks.find(
      (task) => task && task.task_type === "survey_is_anonymous"
    );

    // Only update existing drafts if we're in edit mode OR we have a draft ID AND it's marked as existing draft
    // Do NOT update templates - they should always create new drafts
    const isTemplateData = formData.is_template === true;
    const shouldUpdate = mode === "edit" || (formData.id && !isTemplateData && isExistingDraft);

    if (shouldUpdate) {
      // On edit, we must ensure the updated user_tasks array includes the assignee_draft
      const updatedUserTasks = assigneeDraft;

      // Update the email user_tasks with emailDraft
      const updatedEmailTasks = emailDraft;

      // Update the questions user_tasks with the questions
      const updatedQuestionsTasks = restOfFormData.questions.map(q => ({
        ...q,
        task_type: "question",
        options: q.options || [], // Preserve options array
        type: q.type?.value || q.type, // Handle both object and string type formats
      }));
      
      // Update the description user_tasks with the description
      const updatedDescriptionTasks = { text: restOfFormData?.description, task_type: "description" }

      // Update the title user_tasks with the title
      const updatedTitleTasks = { text: restOfFormData?.name, task_type: "name" }

      // update the survey_is_anonymous user_tasks with the survey_is_anonymous
      const updatedSurveyIsAnonymousTasks = { value: surveyIsAnonymous?.value, task_type: "survey_is_anonymous" }

      // Empty the assignedUsersDraft array
      restOfFormData.assignedUsersDraft = [];

      // Remove user_tasks from restOfFormData
      delete restOfFormData.user_tasks;

      // Get the original created_by task
      const originalCreatedBy = fetchedTemplate?.user_tasks?.find(
        (task) => task && task.task_type === "created_by"
      );

      // Get the original is_draft task to preserve draft status
      const originalIsDraft = fetchedTemplate?.user_tasks?.find(
        (task) => task && task.task_type === "is_draft"
      );

      // Update the user_tasks array with the survey template sort_order
      const payloadData = {
        ...restOfFormData,
        id: surveyId || formData.id, // Use surveyId for edit mode, formData.id for existing drafts
        user_tasks: [
          updatedUserTasks,
          updatedEmailTasks,
          ...updatedQuestionsTasks,
          updatedDescriptionTasks,
          updatedTitleTasks,
          updatedSurveyIsAnonymousTasks,
          collaboratorsDraft,
          // Preserve draft flag if updating a draft, or add it if creating from template as draft
          ...(originalIsDraft ? [originalIsDraft] : (isTemplate === false ? [{ task_type: "is_draft", value: true }] : [])),
          originalCreatedBy || {
            user_id: Number(get_auth?.employee_id),
            name: `${get_auth?.employee?.first_name} ${get_auth?.employee?.last_name}`,
            account_type: get_auth?.employee?.account_type,
            email: get_auth?.employee?.email,
            organization_id: get_auth?.employee?.organization_id,
            task_type: "created_by",
          },
        ],
      };

      dispatch({
        type: SG_UPDATE_TASK_TEMPLATE,
        payload: payloadData,
      });
    } else {
      // Create scenario: Build "created_by"
      const userObj = {
        user_id: Number(get_auth?.employee_id),
        name: `${get_auth?.employee?.first_name} ${get_auth?.employee?.last_name}`,
        account_type: get_auth?.employee?.account_type,
        email: get_auth?.employee?.email,
        organization_id: get_auth?.employee?.organization_id,
        task_type: "created_by",
      };

      const baseUserTasks =
        restOfFormData?.user_tasks?.length > 0 ? restOfFormData.user_tasks : [];

      // Remove the ID from restOfFormData to ensure we create a new draft, not update the template
      const { id, ...dataWithoutId } = restOfFormData;
      
      const payloadData = {
        ...dataWithoutId, // assignedUsersList is no longer included, and ID is removed
        user_tasks: [
          ...baseUserTasks,
          userObj,
          emailDraft,
          assigneeDraft,
          collaboratorsDraft,
          surveyIsAnonymous,
          // Add is_draft flag when creating a draft (isTemplate = false)
          ...(isTemplate === false ? [{ task_type: "is_draft", value: true }] : []),
          ...restOfFormData?.questions.map((q) => ({
            ...q,
            task_type: "question",
            id: uuidv4(),
            options: q.options || [], // Preserve options array
            type: q.type?.value || q.type, // Handle both object and string type formats
          })),
          { text: restOfFormData?.description, task_type: "description" },
        ],
        is_template: isTemplate,
      };

      dispatch({
        type: SG_ADD_TASK_TEMPLATE,
        payload: payloadData,
      });
    }

    addToast("Draft saved successfully", { appearance: "success" });
    setLoading(false);

    dispatch({ type: CLEAR_TASK_TEMPLATE });
    // Reset the sending flag after clearing
    isSendingRef.current = false;
  };


  // Handle submit function
  const handleSubmit = () => {
    // Mark that we're sending a survey, not saving a draft
    isSendingRef.current = true;
    setLoading(true);
    setError(null);

    const validationErrors = validateFormData();
    if (Object.keys(validationErrors).length > 0) {
      setError(validationErrors);
      setLoading(false);
      // Reset the sending flag if validation fails
      isSendingRef.current = false;
      return;
    }

    const { assignedUsersList, assignedUsersDraft, emailBody, emailSubject, collaborators, isExistingDraft, ...restOfFormData } = formData;
    
    // Check if we have a draft ID (either from initial data or from creating a draft)
    // But exclude templates - templates should NEVER be treated as drafts
    const hasDraftTask = formData.user_tasks?.some(task => task.task_type === "is_draft" && task.value === true);
    const isTemplate = formData.is_template === true;
    const isDraft = Boolean(formData.id && !isTemplate && (isExistingDraft || formData.isExistingDraft || hasDraftTask));

    // Common objects
    const emailDraft = {
      task_type: "email_draft",
      subject: restOfFormData.emailSubject || emailSubject,
      body: restOfFormData.emailBody || emailBody,
    };

    // Concatenate the assignedUsersList and assignedUsersDraft removing duplicates
    const updateUserList = assignedUsersList;
    const existingUserList = assignedUsersDraft;

    // If the updateUserList is not empty use it, otherwise use the existingUserList
    const assigneesList = updateUserList.length > 0 ? updateUserList : existingUserList;

    const assigneeDraft = {
      task_type: "assignee_draft",
      assignees: assigneesList,
    };

    const collaboratorsDraft = {
      task_type: "collaborators",
      collaborators: collaborators.map(user => user.value),
    };

    // Add assigned users count for efficient list view display (only when sending survey)
    const assignedUsersCount = {
      task_type: "assigned_users_count",
      count: assigneesList.length,
    };

    const surveyIsAnonymous = restOfFormData.user_tasks.find(
      (task) => task && task.task_type === "survey_is_anonymous"
    );

    if (mode === "edit") {
      // On edit, we must ensure the updated user_tasks array includes the assignee_draft
      const updatedUserTasks = assigneeDraft;

      // Update the email user_tasks with emailDraft
      const updatedEmailTasks = emailDraft;

      // Update the questions user_tasks with the questions
      const updatedQuestionsTasks = restOfFormData.questions.map(q => ({
        ...q,
        task_type: "question",
        options: q.options || [], // Preserve options array
        type: q.type?.value || q.type, // Handle both object and string type formats
      }));
      
      // Update the description user_tasks with the description
      const updatedDescriptionTasks = { text: restOfFormData?.description, task_type: "description" }

      // Update the title user_tasks with the title
      const updatedTitleTasks = { text: restOfFormData?.name, task_type: "name" }

      // update the survey_is_anonymous user_tasks with the survey_is_anonymous
      const updatedSurveyIsAnonymousTasks = { value: surveyIsAnonymous?.value, task_type: "survey_is_anonymous" }

      // Empty the assignedUsersDraft array
      restOfFormData.assignedUsersDraft = [];

      // Remove user_tasks from restOfFormData
      delete restOfFormData.user_tasks;

      const userObj = {
        user_id: Number(get_auth?.employee_id),
        name: `${get_auth?.employee?.first_name} ${get_auth?.employee?.last_name}`,
        account_type: get_auth?.employee?.account_type,
        email: get_auth?.employee?.email,
        organization_id: get_auth?.employee?.organization_id,
        task_type: "created_by",
      };

      // Update the user_tasks array with the survey template sort_order
      const payloadData = {
        ...restOfFormData,
        id: surveyId,
        triggerSend: true,
        user_tasks: [
          updatedUserTasks,
          updatedEmailTasks,
          ...updatedQuestionsTasks,
          updatedDescriptionTasks,
          updatedTitleTasks,
          updatedSurveyIsAnonymousTasks,
          collaboratorsDraft,
          assignedUsersCount,
          userObj,
        ],
      };

      dispatch({
        type: SG_UPDATE_TASK_TEMPLATE,
        payload: payloadData,
      });
    } else if (isDraft) {
      // Handle sending existing draft survey - similar to edit mode but for drafts
      const updatedUserTasks = assigneeDraft;
      const updatedEmailTasks = emailDraft;
      const updatedQuestionsTasks = restOfFormData.questions.map(q => ({
        ...q,
        task_type: "question",
        options: q.options || [],
        type: q.type?.value || q.type,
      }));
      
      const updatedDescriptionTasks = { text: restOfFormData?.description, task_type: "description" }
      const updatedTitleTasks = { text: restOfFormData?.name, task_type: "name" }
      const updatedSurveyIsAnonymousTasks = { value: surveyIsAnonymous?.value, task_type: "survey_is_anonymous" }

      // Empty the assignedUsersDraft array
      restOfFormData.assignedUsersDraft = [];
      delete restOfFormData.user_tasks;

      const userObj = {
        user_id: Number(get_auth?.employee_id),
        name: `${get_auth?.employee?.first_name} ${get_auth?.employee?.last_name}`,
        account_type: get_auth?.employee?.account_type,
        email: get_auth?.employee?.email,
        organization_id: get_auth?.employee?.organization_id,
        task_type: "created_by",
      };

      const payloadData = {
        ...restOfFormData,
        id: formData.id, // Use the draft's existing ID
        triggerSend: true,
        user_tasks: [
          updatedUserTasks,
          updatedEmailTasks,
          ...updatedQuestionsTasks,
          updatedDescriptionTasks,
          updatedTitleTasks,
          updatedSurveyIsAnonymousTasks,
          collaboratorsDraft,
          assignedUsersCount,
          userObj,
        ],
      };

      dispatch({
        type: SG_UPDATE_TASK_TEMPLATE,
        payload: payloadData,
      });
    } else {
      // Build the user object
      const userObj = {
        user_id: Number(get_auth?.employee_id),
        name: `${get_auth?.employee?.first_name} ${get_auth?.employee?.last_name}`,
        account_type: get_auth?.employee?.account_type,
        email: get_auth?.employee?.email,
        organization_id: get_auth?.employee?.organization_id,
        task_type: "created_by",
      };
      setLoadingMessage("Creating survey template...");

      // Prepare payload for survey creation
      const payloadData = {
        ...formData,
        user_tasks:
          formData.user_tasks?.length > 0
            ? [
                ...formData.user_tasks,
                userObj,
                assignedUsersCount,
                ...formData?.questions.map((q) => ({
                  ...q,
                  task_type: "question",
                  id: uuidv4(),
                  options: q.options || [], // Preserve options array
                  type: q.type?.value || q.type, // Handle both object and string type formats
                })),
                {
                  text: formData?.description,
                  task_type: "description",
                  options: [], // Preserve options array
                  type: "text", // Handle string type format
              },
              ]
            : [
                userObj,
                assignedUsersCount,
                ...formData?.questions.map((q) => ({
                  ...q,
                  task_type: "question",
                  id: uuidv4(),
                  options: q.options || [], // Preserve options array
                  type: q.type?.value || q.type, // Handle both object and string type formats
                })),
                {
                  text: formData?.description,
                  task_type: "description",
                  options: [], // Preserve options array
                  type: "text", // Handle string type format
              },
            ],
        is_template: false
      };

      // Add a timeout to simulate clean loading state
      // Dispatch action to create the survey/template
      setTimeout(() => {
        dispatch({
          type: SG_ADD_TASK_TEMPLATE,
          payload: payloadData,
        });
      }, 500);
    }
  };

  // Effect to watch for survey creation
  // Only trigger when sending a survey (via handleSubmit), NOT when saving a draft
  useEffect(() => {
    // Only proceed if we're sending (not saving draft) AND we have a template ID
    if (isSendingRef.current && ((createdTemplate?.id && mode !== "edit") || draftSend?.id)) {
      // Survey created, now schedule it
      const payloadObj = {
        task_template_user_tasks: createdTemplate?.id || draftSend?.id,
        name: formData.name,
        assigned_employees: [],
      };

      setLoadingMessage("Sending survey to assignees list...");

      setTimeout(() => {
        dispatch({
          type: SG_CREATE_TASK_SCHEDULE,
          payload: payloadObj,
        });
      }, 500);
    }
  }, [createdTemplate, mode, draftSend]);

  // Effect to watch for scheduling completion
  // Only trigger when sending a survey (via handleSubmit), NOT when saving a draft
  useEffect(() => {
    if (isSendingRef.current && createdScheduledTemplate?.id && (mode !== "edit" || draftSend?.id)) {
      // Create or update the email payload
      const emailPayload = {
        email_subject: formData.emailSubject,
        email_content: formData.emailBody,
        task_template: createdScheduledTemplate.id,
      };

      setLoadingMessage("Creating email template...");

      setTimeout(() => {
        dispatch({
          type: SG_ADD_EMAIL_TEMPLATE,
          payload: emailPayload,
        });
      }, 250);
    }
  }, [createdScheduledTemplate, mode, draftSend]);

  // Effect to watch for email template creation
  // Only trigger when sending a survey (via handleSubmit), NOT when saving a draft
  // This prevents SG_ADD_USER_TASK from firing during draft saves
  useEffect(() => {
    if (isSendingRef.current && createdEmailTemplate?.id && (mode !== "edit" || draftSend?.id)) {
      const listOne =
        formData?.assignedUsersList?.map((user) => user.key) || [];
      const draftList =
        formData?.assignedUsersDraft?.map((user) => user.key) || [];

      let userList = []
      if (listOne?.length > 0) {
        userList = listOne;
      } else {
        userList = draftList;
      }

      // Remove any duplicates from the finalList
      const uniqueList = [...new Set(userList)];

      // Assign users to the scheduled task
      const userTaskPayload = {
        task_id: createdScheduledTemplate?.id,
        employee_id_list: uniqueList,
        id: createdTemplate?.id || draftSend?.id || fetchedTemplate?.id,
        task_template_user_tasks: createdTemplate?.id || draftSend?.id || fetchedTemplate?.id
      };

      setLoadingMessage("Assigning users to the survey...");

      dispatch({
        type: SG_ADD_USER_TASK,
        payload: userTaskPayload,
      });
    }
  }, [createdEmailTemplate, mode, draftSend, createdTemplate]);


  // Effect to watch for user task assignment
  useEffect(() => {
    if (
      assignedUserTasks.includes(fetchedTemplate?.id) ||
      assignedUserTasks?.includes(createdTemplate?.id) ||
      assignedUserTasks?.includes(draftSend?.id) ||
      assignedUserTasks?.includes(createdScheduledTemplate?.id)
    ) {
      // Check if we have collaborators to add report access for
      const collaboratorIds = formData?.collaborators?.map(user => user.value || user.id) || [];
      
      if (collaboratorIds.length > 0) {
        // Add collaborators to report access in the background
        dispatch({
          type: SG_ADD_EMPLOYEE_REPORT_ACCESS,
          payload: {
            task_id: createdScheduledTemplate?.id,
            employee_id_list: collaboratorIds,
          },
        });
      }
      
      // Always proceed to completion after user assignment - don't wait for report access
      completeSurvey();
    }
  }, [assignedUserTasks]);

  // Helper function to complete survey creation
  const completeSurvey = () => {
    // First update the loading state
    setLoading(false);
    // Reset the sending flag
    isSendingRef.current = false;

    // Add a small delay to ensure state updates are reflected
    setTimeout(() => {
      // Clear navigation state
      dispatch({ type: CLEAR_TASK_TEMPLATE });
      // Show success message
      addToast("Survey created successfully", { appearance: "success" });
      // Navigate after state updates
      // Ensure OTP gets routed properly as /app/otp
      // but if its already got leader in the url then navigate to /leader/audit/schedule-v2
      if (window.location.pathname.includes("leader")) {
        navigate(`/leader/audit/schedule-v2`);
      } else {
        navigate(`/app/otp/AI/pulse-survey`);
      }
    }, 100);
  };

  // Effect to watch for survey update
  useEffect(() => {
    if (updatedTemplate?.id && mode === "edit" && !draftSend?.id) {
      // Check if we have collaborators to add report access for in edit mode
      const collaboratorIds =
        formData?.collaborators?.map((user) => user.value || user.id) || [];

      if (collaboratorIds.length > 0) {
        // Add collaborators to report access in the background
        dispatch({
          type: SG_ADD_EMPLOYEE_REPORT_ACCESS,
          payload: {
            task_id: updatedTemplate.id,
            employee_id_list: collaboratorIds,
          },
        });
      }

      // Always complete the edit operation - don't wait for report access
      completeEditOperation();
    }
  }, [updatedTemplate, mode]);

  // Helper function to complete edit operation
  const completeEditOperation = () => {
    setLoading(false);
    // Reset the sending flag
    isSendingRef.current = false;
    // Ensure OTP gets routed properly as /app/otp
    // but if its already got leader in the url then navigate to /leader/audit/schedule-v2
    if (window.location.pathname.includes("leader")) {
      navigate(`/leader/audit/schedule-v2`);
    } else {
      navigate(`/app/otp/AI/pulse-survey`);
    }
    addToast("Survey updated successfully", { appearance: "success" });
  };

  return (
    <SurveyContext.Provider
      value={{
        formData,
        updateFormData,
        handleSubmit,
        saveDraft,
        loading,
        loadingMessage,
        error,
      }}
    >
      {children}
    </SurveyContext.Provider>
  );
};